<template>
  <AppLayout>
    <template #header>
      <PageHeadingWithActionsAndBreadcrumbs
        :title="`People on ${props.plane.tail_number}`"
        :breadcrumbs="breadcrumbs"
      >
        <template #actions>
          <ButtonBase :leadingIcon="PlusSmIcon" @click="showingAddModal = true"
            >Add Person</ButtonBase
          >
        </template>
      </PageHeadingWithActionsAndBreadcrumbs>
    </template>

    <div class="space-y-6">
      <TableBase v-for="group in groups" :key="group.type">
        <template #before>
          <CardHeadingWithDescriptionAction
            :title="group.title"
            :description="group.description"
          />
        </template>

        <template v-if="group.members.length" #tbody>
          <tr v-for="member in group.members" :key="member.id">
            <TableCellBase bold>
              <div class="flex items-center">
                <AvatarUser :user="member" size="sm" />
                <span class="ml-3">{{ member.name }}</span>
              </div>
            </TableCellBase>
            <TableCellBase>{{ member.email || 'No email' }}</TableCellBase>
            <TableCellBase>
              <span v-if="member.owner_name"
                >Bills to {{ member.owner_name }}</span
              >
              <BadgeBase v-if="!member.has_login" color="yellow" class="ml-2"
                >No login</BadgeBase
              >
            </TableCellBase>
            <TableCellBase right>
              <LinkBase @click="removing = member">Remove</LinkBase>
            </TableCellBase>
          </tr>
        </template>

        <template v-else #after>
          <CardPadding>
            <ParagraphText color="text-gray-500"
              >Nobody has this role yet.</ParagraphText
            >
          </CardPadding>
        </template>
      </TableBase>
    </div>

    <ModalSimpleAlertWithDismiss
      @confirm="remove"
      @close="removing = null"
      :show="!!removing"
      :processing="removeProcessing"
      title="Remove role"
      :description="removeDescription"
      confirmLabel="Remove"
    />

    <AddMemberModal
      :show="showingAddModal"
      @close="showingAddModal = false"
      :plane="props.plane"
      :owners="props.owners"
      :types="props.types"
    />

    <CredentialsModal />
  </AppLayout>
</template>

<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHeadingWithActionsAndBreadcrumbs from '@/Common/Headings/PageHeadings/PageHeadingWithActionsAndBreadcrumbs.vue'
import ButtonBase from '@/Common/Elements/Buttons/ButtonBase.vue'
import TableBase from '@/Common/Lists/Tables/TableBase.vue'
import TableCellBase from '@/Common/Lists/Tables/TableCellBase.vue'
import CardHeadingWithDescriptionAction from '@/Common/Headings/CardHeadings/CardHeadingWithDescriptionAction.vue'
import CardPadding from '@/Common/Layout/Containers/CardPadding.vue'
import ParagraphText from '@/Common/Text/ParagraphText.vue'
import AvatarUser from '@/Common/Elements/Avatars/AvatarUser.vue'
import BadgeBase from '@/Common/Elements/Badges/BadgeBase.vue'
import LinkBase from '@/Common/Text/LinkBase.vue'
import ModalSimpleAlertWithDismiss from '@/Common/Overlays/Modals/ModalSimpleAlertWithDismiss.vue'
import AddMemberModal from './AddMemberModal.vue'
import CredentialsModal from './CredentialsModal.vue'
import { PlusSmIcon } from '@heroicons/vue/solid'
import { Inertia } from '@inertiajs/inertia'
import { computed, ref } from 'vue'
import { roleMeta } from './roles'

const props = defineProps({
  plane: Object,
  members: Array,
  owners: Array,
  types: Array,
})

const breadcrumbs = [
  {
    title: props.plane.tail_number,
    href: route('planes.show', props.plane),
  },
]

const groups = computed(() =>
  props.types.map((type) => ({
    type,
    ...roleMeta[type],
    members: props.members.filter((member) => member.type === type),
  }))
)

// Add
const showingAddModal = ref(false)

// Remove
const removing = ref(null)
const removeProcessing = ref(false)

const removeDescription = computed(() =>
  removing.value
    ? `Remove ${removing.value.name} as a ${removing.value.type} of ${props.plane.tail_number}? Any other roles they hold on this plane are kept.`
    : ''
)

function remove() {
  Inertia.delete(
    route('planes.users.destroy', {
      plane: props.plane.tail_number,
      user: removing.value.id,
      type: removing.value.type,
    }),
    {
      preserveScroll: true,
      onStart: () => (removeProcessing.value = true),
      onFinish: () => {
        removeProcessing.value = false
        removing.value = null
      },
    }
  )
}
</script>
